import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { TabShell } from "@/features/settings/components/tab-shell";
import {
  AddTagDialog,
  AddTagGroupDialog,
  DeleteCategoryDialog,
  DeleteTagDialog,
  RenameCategoryDialog,
  RenameTagDialog,
} from "@/features/settings/components/tabs/tags-tab-dialogs";
import {
  archiveTag,
  listTagGroups,
  listTags,
  reorderTags,
  type Tag,
  type TagGroup,
} from "@/features/settings/tags-api";
import { ApiError } from "@/lib/api";

export function TagsTab() {
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const [groups, setGroups] = useState<TagGroup[] | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [error, setError] = useState(false);

  // Dialog state (each holds the record it's acting on).
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [addTagGroup, setAddTagGroup] = useState<TagGroup | null>(null);
  const [renameTag, setRenameTag] = useState<Tag | null>(null);
  const [deleteTag, setDeleteTag] = useState<Tag | null>(null);
  const [renameGroup, setRenameGroup] = useState<TagGroup | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<TagGroup | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([listTagGroups(), listTags()])
      .then(([g, t]) => {
        if (!active) return;
        setGroups(g);
        setTags(t);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  // Firm-wide tag order (matches the reorder endpoint's whole-set contract).
  const orderedTags = useMemo(
    () => [...tags].sort((a, b) => a.position - b.position),
    [tags],
  );

  const groupsSorted = useMemo(
    () => (groups ? [...groups].sort((a, b) => a.position - b.position) : []),
    [groups],
  );

  function upsertTag(saved: Tag) {
    setTags((prev) => {
      const idx = prev.findIndex((t) => t.id === saved.id);
      if (idx === -1) return [...prev, saved];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
  }

  function removeTag(id: string) {
    setTags((prev) => prev.filter((t) => t.id !== id));
  }

  function upsertGroup(saved: TagGroup) {
    setGroups((prev) => {
      if (!prev) return [saved];
      const idx = prev.findIndex((g) => g.id === saved.id);
      if (idx === -1) return [...prev, saved];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
  }

  function removeGroup(id: string) {
    setGroups((prev) => prev?.filter((g) => g.id !== id) ?? null);
  }

  async function handleArchiveToggle(tag: Tag) {
    try {
      const updated = await archiveTag(tag.id);
      upsertTag(updated);
      toast.success(updated.isArchived ? "Tag archived" : "Tag unarchived");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the tag.");
    }
  }

  // Move a tag up/down in the firm-wide order, then persist the new id list.
  async function handleMove(tag: Tag, delta: number) {
    const order = orderedTags.map((t) => t.id);
    const index = order.indexOf(tag.id);
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target]!, order[index]!];
    try {
      const updated = await reorderTags(order);
      setTags(updated);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't reorder tags.");
    }
  }

  const tagsByGroup = useMemo(() => {
    const map = new Map<string, Tag[]>();
    for (const t of orderedTags) {
      const list = map.get(t.groupId) ?? [];
      list.push(t);
      map.set(t.groupId, list);
    }
    return map;
  }, [orderedTags]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Client Tags"
        subtitle="Organize clients with custom tags, grouped into categories. Tags can be applied and filtered on the Clients screen."
        actions={
          isAdmin && groups ? (
            <Button type="button" size="sm" className="gap-1.5" onClick={() => setAddGroupOpen(true)}>
              <Plus className="size-4" />
              New Tag Group
            </Button>
          ) : undefined
        }
      />
      <TabShell loading={!groups && !error} error={error}>
        {groups && (
        <div className="flex flex-col gap-6">
          {groupsSorted.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No tag categories yet.{" "}
              {isAdmin ? "Create one to start tagging clients." : ""}
            </p>
          ) : (
            groupsSorted.map((group) => {
              const groupTags = tagsByGroup.get(group.id) ?? [];
              return (
                <div key={group.id} className="rounded-lg border">
                  <div className="bg-muted/40 flex items-center justify-between gap-2 border-b px-4 py-3">
                    <h3 className="text-foreground font-semibold">{group.name}</h3>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Rename ${group.name}`}
                          onClick={() => setRenameGroup(group)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={`Delete ${group.name}`}
                          disabled={groupTags.length > 0}
                          title={
                            groupTags.length > 0
                              ? "Delete or move this group's tags first"
                              : undefined
                          }
                          onClick={() => setDeleteGroup(group)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 p-4">
                    {groupTags.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No tags in this category.</p>
                    ) : (
                      groupTags.map((tag) => {
                        const globalIndex = orderedTags.findIndex((t) => t.id === tag.id);
                        return (
                          <div
                            key={tag.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="secondary"
                                className={tag.isArchived ? "opacity-60" : undefined}
                              >
                                {tag.name}
                              </Badge>
                              {tag.isArchived && (
                                <Badge variant="outline" className="text-muted-foreground">
                                  Archived
                                </Badge>
                              )}
                            </div>
                            {isAdmin && (
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`Move ${tag.name} up`}
                                  disabled={globalIndex <= 0}
                                  onClick={() => void handleMove(tag, -1)}
                                >
                                  <ChevronUp className="size-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`Move ${tag.name} down`}
                                  disabled={globalIndex >= orderedTags.length - 1}
                                  onClick={() => void handleMove(tag, 1)}
                                >
                                  <ChevronDown className="size-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => void handleArchiveToggle(tag)}
                                >
                                  {tag.isArchived ? "Unarchive" : "Archive"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setRenameTag(tag)}
                                >
                                  Rename
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setDeleteTag(tag)}
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {isAdmin && (
                      <div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setAddTagGroup(group)}
                        >
                          <Plus className="size-4" /> New Tag
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {!isAdmin && (
            <p className="text-muted-foreground text-sm">
              Only a country director can manage tags.
            </p>
          )}
        </div>
      )}

      {isAdmin && (
        <>
          <AddTagGroupDialog
            open={addGroupOpen}
            onOpenChange={setAddGroupOpen}
            onSaved={upsertGroup}
          />
          <AddTagDialog
            key={`add-tag-${addTagGroup?.id ?? "none"}`}
            group={addTagGroup}
            open={addTagGroup !== null}
            onOpenChange={(open) => !open && setAddTagGroup(null)}
            onSaved={upsertTag}
          />
          <RenameTagDialog
            key={`rename-tag-${renameTag?.id ?? "none"}`}
            tag={renameTag}
            open={renameTag !== null}
            onOpenChange={(open) => !open && setRenameTag(null)}
            onSaved={upsertTag}
          />
          <DeleteTagDialog
            tag={deleteTag}
            open={deleteTag !== null}
            onOpenChange={(open) => !open && setDeleteTag(null)}
            onDeleted={removeTag}
          />
          <RenameCategoryDialog
            key={`rename-cat-${renameGroup?.id ?? "none"}`}
            group={renameGroup}
            open={renameGroup !== null}
            onOpenChange={(open) => !open && setRenameGroup(null)}
            onSaved={upsertGroup}
          />
          <DeleteCategoryDialog
            group={deleteGroup}
            open={deleteGroup !== null}
            onOpenChange={(open) => !open && setDeleteGroup(null)}
            onDeleted={removeGroup}
          />
        </>
      )}
      </TabShell>
    </div>
  );
}
